/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useCallback, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Grid,
  useTheme,
  useMediaQuery,
  Container,
  Divider,
  Dialog,
} from "@mui/material";
import GppGoodIcon from '@mui/icons-material/GppGood';
import { useFirebase } from "contexts/Firebase";
import { useAuth } from "contexts/User";
import { useCollection } from "react-firebase-hooks/firestore";
import {
  collection,
  orderBy,
  query,
  getDoc,
  setDoc,
  updateDoc,
  doc,
  getDocs,
} from "firebase/firestore";
import { QuotitHealthPlanDetails } from "services/Interfaces";
import { QuotitPlanConverter } from "services/Firestore/Plan";
import { useLazyQuery } from "@apollo/client";
import { Queries } from "services/apollo";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import ViewDetailsModalQuotit from "components/HealthPlan/ViewDetailsModalQuotit";
import PlanOptionsJamieAI from "./PlanOptionsJamieAI";
// import SelectPlanModalQuotit from "components/HealthPlan/SelectPlanModalQuotit";
import PropTypes from "prop-types";
import BenefitPlanDetailsDialog from "./BenefitPlanDetailsDialog";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import BadgeIcon from '@mui/icons-material/Badge';
import PlanSelectionDialog from "./PlanSelectionDialog";

interface RecommendationsProps {
  sendPrompt: (message: string) => void;
  conversationId?: string;
  onPlanSelect?: (planDetails: any) => void;
}

interface PremiumTier {
  employeeContribution: number;
  employerContribution: number;
  totalPremium: number;
}

interface FourTierPremium {
  employeeOnly: PremiumTier;
  employeePlusChildren: PremiumTier;
  employeePlusSpouse: PremiumTier;
  family: PremiumTier;
}

interface BenefitPlan {
  id: string;
  name: string;
  carrier: string;
  individualDeductible: number;
  familyDeductible: number;
  individualOutOfPocketMaximum: number;
  familyOutOfPocketMaximum: number;
  fourTierPremium: FourTierPremium;
  coverageTierStructure: string;
}

interface CurrentBenefitPlanProps {
  conversationId?: string;
}

// Add interface for message structure
interface ConversationMessage {
  data?: {
    selectedBenefitPlan?: {
      id: string;
      name: string;
      carrier: string;
      planNetworkType: string;
      individualDeductible: number;
      familyDeductible: number;
      individualOutOfPocketMaximum: number;
      familyOutOfPocketMaximum: number;
    };
  };
}

// Add new component for current benefit plan display
const CurrentBenefitPlan: React.FC<CurrentBenefitPlanProps> = React.memo(
  ({ conversationId }) => {
    const { firestore } = useFirebase();
    const { user } = useAuth();
    const [benefitPlan, setBenefitPlan] = useState<any>(null);
    const [allBenefitPlans, setAllBenefitPlans] = useState<any[]>([]);
    const [planDetailsOpen, setPlanDetailsOpen] = useState(false);
    const [updatePlanOpen, setUpdatePlanOpen] = useState(false);
    const [currentSettings, setCurrentSettings] = useState<any>({
      insuranceStatus: null,
      effectiveDate: null,
      selectedPlan: null,
      whenBenefits: [],
      benefitPlan: null,
    });
    const [selectedPlanId, setSelectedPlanId] = useState("");

    useEffect(() => {
      const fetchBenefitPlan = async () => {
        if (!user?.uid) return;

        try {
          const chatSettingsRef = doc(
            firestore,
            `users/${user.uid}/settings/chat`,
          );
          const chatSettingsSnapshot = await getDoc(chatSettingsRef);

          if (chatSettingsSnapshot.exists()) {
            const chatSettings = chatSettingsSnapshot.data();
            if (chatSettings.selectedBenefitPlan) {
              setBenefitPlan(chatSettings.selectedBenefitPlan);
              setSelectedPlanId(chatSettings.selectedBenefitPlan.id || "");
              setCurrentSettings({
                ...currentSettings,
                benefitPlan: chatSettings.selectedBenefitPlan.name,
              });
            }
          }
        } catch (error) {
          console.error("Error fetching benefit plan:", error);
        }
      };

      fetchBenefitPlan();
    }, [user?.uid, firestore, currentSettings]);

    useEffect(() => {
      const fetchAllBenefitPlans = async () => {
        try {
          const benefitPlansRef = collection(firestore, 'companies/when/benefitPlans');
          const benefitPlansSnapshot = await getDocs(benefitPlansRef);
          const plans = benefitPlansSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
          }));
          setAllBenefitPlans(plans);
        } catch (error) {
          console.error("Error fetching all benefit plans:", error);
        }
      };

      fetchAllBenefitPlans();
    }, [firestore]);

    const handlePlanSelect = (planId: string) => {
      setSelectedPlanId(planId);
    };

    if (!benefitPlan) return null;

    return (
      <>
        <Box
          sx={{
            borderRadius: "12px",
            overflow: "hidden",
            boxShadow: 1,
            backgroundColor: "#FFFFFF",
            border: "1px solid #E0E0E0",
          }}
        >
          <Grid container>
            {/* Left Column: Details - Contains nested rows */}
            <Grid item xs={12} sm={8} container sx={{ borderRight: { sm: '1px solid #E0E0E0' } }}>
              {/* Logo/Name/Insurer Row */}
              <Grid item xs={12} sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
                <Box sx={{ width: 50, height: 50, bgcolor: '#fffff', borderRadius: 1, mr: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <BadgeIcon sx={{ fontSize: '2rem', color: '#395B50' }} />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {benefitPlan.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Insurer: {benefitPlan.carrier}
                  </Typography>
                </Box>
              </Grid>

              {/* Divider Row (Inside Left Column) */}
              <Grid item xs={12}>
                <Divider />
              </Grid>

              {/* Bottom Details Row (Inside Left Column) */}
              <Grid item xs={12} container>
                {/* Premium & Deductible/OOP */}
                <Grid item xs={12} md={6} sx={{ p: 2 }}> {/* Adjusted grid size to 6 */}
                  <Box sx={{ p: 2, bgcolor: '#f7f7f7', borderRadius: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Monthly premium</Typography>
                      <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
                        ${benefitPlan.fourTierPremium?.employeeOnly?.totalPremium?.toLocaleString() || "0"}
                        <InfoOutlinedIcon sx={{ fontSize: '1rem', ml: 0.5, color: 'text.secondary' }} />
                      </Typography>
                    </Box>
                    <Grid container spacing={1} sx={{ mt: 1 }}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Deductible</Typography>
                        <Typography variant="body1" fontWeight="bold">${benefitPlan.individualDeductible?.toLocaleString()}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Out of Pocket Max</Typography>
                        <Typography variant="body1" fontWeight="bold">${benefitPlan.individualOutOfPocketMaximum?.toLocaleString()}</Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>

                {/* Plan Type, Insurance Type, Score, Rating */}
                <Grid item xs={12} md={6} container sx={{ p: 2 }}> {/* Adjusted grid size to 6 */}
                  <Grid item xs={6} sm={6} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', p:1 }}>
                    <Typography variant="body2" color="text.secondary">Plan Type</Typography>
                    <Typography variant="body1" fontWeight="bold">{benefitPlan.planNetworkType}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={6} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', p:1 }}>
                    <Typography variant="body2" color="text.secondary">Insurance Type</Typography>
                    <Typography variant="body1" fontWeight="bold">COBRA</Typography>
                  </Grid>
                  <Grid item xs={6} sm={6} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', p:1 }}>
                    <Typography variant="body2" color="text.secondary">Score</Typography>
                    <Typography variant="h6" fontWeight="bold">54</Typography>
                  </Grid>
                  <Grid item xs={6} sm={6} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', p:1 }}>
                    <Typography variant="body2" color="text.secondary">Star Rating</Typography>
                    <Box>
                       <StarBorderIcon sx={{ fontSize: '1.2rem', color: 'text.disabled' }} />
                       <StarBorderIcon sx={{ fontSize: '1.2rem', color: 'text.disabled' }} />
                       <StarBorderIcon sx={{ fontSize: '1.2rem', color: 'text.disabled' }} />
                       <StarBorderIcon sx={{ fontSize: '1.2rem', color: 'text.disabled' }} />
                       <StarBorderIcon sx={{ fontSize: '1.2rem', color: 'text.disabled' }} />
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            {/* Right Column: Update Button Area */}
            <Grid item xs={12} sm={4} sx={{ p: 2, backgroundColor: '#f7f7f7', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
              <Button 
                variant="contained" 
                sx={{ bgcolor: '#395B50', '&:hover': { bgcolor: '#2a433a'} }}
                onClick={() => setUpdatePlanOpen(true)}
              >
                Update
              </Button>
              <Button
                variant="text"
                color="primary"
                onClick={() => setPlanDetailsOpen(true)}
                sx={{
                  textDecoration: "underline",
                  "&:hover": {
                    textDecoration: "underline",
                    backgroundColor: 'transparent'
                  },
                  p: 0, // Remove default padding for text button if needed
                  mt: 0.5, // Add small margin top
                }}
              >View Plan Details</Button>
            </Grid>
          </Grid>
        </Box>

        <BenefitPlanDetailsDialog
          open={planDetailsOpen}
          onClose={() => setPlanDetailsOpen(false)}
          selectedPlan={benefitPlan.id}
          onConfirm={() => setPlanDetailsOpen(false)}
          benefitPlans={[benefitPlan]}
          household={null}
        />

        <PlanSelectionDialog
          open={updatePlanOpen}
          onClose={() => setUpdatePlanOpen(false)}
          selectedPlan={selectedPlanId}
          onPlanSelect={handlePlanSelect}
          onConfirm={() => setUpdatePlanOpen(false)}
          benefitPlans={allBenefitPlans.length > 0 ? allBenefitPlans : [benefitPlan]}
          household={null}
          setCurrentSettings={setCurrentSettings}
        />
      </>
    );
  },
);

// Add prop types validation
CurrentBenefitPlan.propTypes = {
  conversationId: PropTypes.string,
};

CurrentBenefitPlan.displayName = "CurrentBenefitPlan";

// Helper function to determine badge color based on rank
const getBadgeColor = (rank: number): string | undefined => {
  switch (rank) {
    case 0:
      return 'gold'; // Gold for the 1st place
    case 1:
      return 'silver'; // Silver for the 2nd place
    case 2:
      return '#cd7f32'; // Bronze for the 3rd place (hex code for bronze)
    default:
      return undefined; // No badge for others
  }
};

const Recommendations: React.FC<RecommendationsProps> = ({
  sendPrompt,
  conversationId,
  onPlanSelect
}) => {
  const { firestore } = useFirebase();
  const { user, answers, userData } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [selectedPlanForModal, setSelectedPlanForModal] =
    React.useState<any>(null);
  const [planDetailsOpen, setPlanDetailsOpen] = React.useState<boolean>(false);
  const [selectPlanOpen, setSelectPlanOpen] = React.useState<boolean>(false);
  const [imageError, setImageError] = React.useState<boolean>(false);

  const [loadRates, rateQuery] = useLazyQuery<
    { total: number },
    { effectiveDate: string }
  >(Queries.getRates, { notifyOnNetworkStatusChange: true });

  const handleImageError = () => {
    setImageError(true);
  };

  const handlePlanSelection = async (planId: string, planDetails: any) => {
    if (!user?.uid || !conversationId) {
      console.error("User is not authenticated or conversation ID is missing");
      return;
    }

    try {
      await setDoc(
        doc(firestore, "users", user.uid, "settings", "chat"),
        {
          selectedPlan: planId,
          selectedPlanDetails: planDetails,
        },
        { merge: true },
      );

      const convoRef = doc(
        firestore,
        `users/${user.uid}/convos/${conversationId}`,
      );
      const convoSnapshot = await getDoc(convoRef);

      if (convoSnapshot.exists()) {
        const convoData = convoSnapshot.data();
        const messages = convoData.messages || [];

        if (messages.length > 0) {
          const lastMessage = messages[messages.length - 1];
          lastMessage.selectedPlanId = planId;

          await updateDoc(convoRef, {
            messages: messages,
          });
        }
      }
    } catch (error) {
      console.error("Error updating selected plan:", error);
    }

    sendPrompt("I have selected a plan and I would like to enroll.");
  };

  const determinePremiumTier = useCallback((members: any) => {
    const memberCount = Object.keys(members || {}).length;
    const hasSpouse = Object.values(members || {}).some(
      (member: any) => member.relationship === "spouse",
    );
    const hasChildren = Object.values(members || {}).some(
      (member: any) => member.relationship === "child",
    );

    if (memberCount === 1) return "employeeOnly";
    if (hasSpouse && hasChildren) return "family";
    if (hasSpouse) return "employeePlusSpouse";
    if (hasChildren) return "employeePlusChildren";
    return "employeeOnly";
  }, []);

  const capitalizeFirstLetter = (string: string) => {
    return string?.charAt(0).toUpperCase() + string?.slice(1);
  };

  const [plans, plansLoading, plansError] =
    useCollection<QuotitHealthPlanDetails>(
      query<QuotitHealthPlanDetails>(
        collection(
          firestore,
          `users/${user?.uid}/effectiveDate/${answers?.get("insuranceDetails.effectiveDate")}/plans`,
        ).withConverter(QuotitPlanConverter),
        orderBy("recommendation.internalScore", "desc"),
      ),
    );

  if (plansLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (plansError) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography color="error">Error loading plans</Typography>
      </Box>
    );
  }

  const household = answers?.get("insuranceDetails.household.members");
  const premiumTier = determinePremiumTier(household);
  const topThreePlans = plans?.docs
    .filter((plan) => plan.get("insuranceType") === "HealthOnExchange")
    .slice(0, 3);

  return (
    <Box sx={{ backgroundColor: "#F7F7F7", height: "auto", py: 4, borderRadius: 3 }}>
      <Container>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
            Your current plan
          </Typography>
          <CurrentBenefitPlan conversationId={conversationId} />
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
            Recommendations
          </Typography>
          <PlanOptionsJamieAI
            effectiveDate={answers?.get("insuranceDetails.effectiveDate") || moment().format("YYYY-MM-DD")}
            speed={3000}
            cobraEstimate={answers?.get("insuranceDetails.cobraEstimate") || 0}
            conversationId={conversationId}
            sendPrompt={sendPrompt}
            onPlanSelect={onPlanSelect}
          />
        </Box>

        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12} sm={6}>
            <Box
              sx={{
                p: 2,
                bgcolor: "#F0F0F0",
                border: "1px solid #E0E0E0",
                borderRadius: 2,
                boxShadow: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                justifyContent: "center",
                gap: 1,
                textAlign: 'left',
                height: '120px'
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontSize: '1.1rem'
                }}
              >
                Nothing strike your fancy?
              </Typography>
              <Button
                variant="outlined"
                onClick={async () => {
                  sendPrompt("I would like to update my preferences.");
                }}
                sx={{
                  textTransform: 'none',
                  width: { xs: '100%', sm: '220px' },
                  maxWidth: '100%',
                  fontWeight: 'bold',
                  bgcolor: '#FFFFFF',
                  borderWidth: 3,
                  fontSize: '1rem',
                  '&:hover': {
                    borderWidth: 3,
                    bgcolor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              >
                Update Preferences
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box
              sx={{
                p: 2,
                bgcolor: "#F0F0F0",
                border: "1px solid #E0E0E0",
                borderRadius: 2,
                boxShadow: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                justifyContent: "center",
                gap: 1,
                textAlign: 'left',
                height: '120px'
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontSize: '1.1rem'
                }}
              >
                Looking for a full list?
              </Typography>
              <Button
                variant="outlined"
                onClick={() => navigate("/account/insurance-plans")}
                sx={{
                  textTransform: 'none',
                  width: { xs: '100%', sm: '220px', md: '260px' },
                  maxWidth: '100%',
                  fontWeight: 'bold',
                  bgcolor: '#FFFFFF',
                  borderWidth: 3,
                  fontSize: '1rem',
                  whiteSpace: { xs: 'normal', sm: 'nowrap' },
                  padding: { xs: '6px 16px', sm: '6px 16px' },
                  '&:hover': {
                    borderWidth: 3,
                    bgcolor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              >
                View All Plans
              </Button>
            </Box>
          </Grid>
        </Grid>

        {rateQuery.loading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 2,
              mt: 4,
              p: 2,
              bgcolor: "background.paper",
              borderRadius: 2,
              boxShadow: 1,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: "1rem", sm: "1.25rem" },
              }}
            >
              Refreshing Plans
            </Typography>
            <CircularProgress size={24} />
          </Box>
        )}
      </Container>

      <Dialog
        open={planDetailsOpen}
        onClose={() => setPlanDetailsOpen(false)}
        fullWidth
        maxWidth="lg"
      >
        <ViewDetailsModalQuotit
          plan={selectedPlanForModal}
          close={() => setPlanDetailsOpen(false)}
        />
      </Dialog>
    </Box>
  );
};

export default Recommendations;
